import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useSnackbar} from '../context/SnackbarContextProvider';
import QUERY_KEYS from '../constants/queryKeys';
import {
  createFormParams,
  FormResponsePayload,
  FormStatuses,
  handleBulkChangeFormStatusService,
  handleCreateFormService,
  handleSubmitFormResponseService,
  handleShareFormService,
  handleUpdateFormService,
  ShareFormPayload,
} from '../services/operations/forms';

export const useFormsMutations = () => {
  const queryClient = useQueryClient();
  const {showSnackbar} = useSnackbar();

  const changeFormStatusMutation = useMutation({
    mutationFn: (payload: {formIds: string[]; newStatus: FormStatuses}) =>
      handleBulkChangeFormStatusService(payload),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_FORMS],
      });

      showSnackbar({
        message: data?.message,
        type: 'success',
      });
    },
  });

  const createFormMutation = useMutation({
    mutationFn: (payload: createFormParams) => handleCreateFormService(payload),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_FORMS],
      });

      showSnackbar({
        message: data?.message,
        type: 'success',
      });
    },
  });

  const updateFormMutation = useMutation({
    mutationFn: ({
      formId,
      payload,
    }: {
      formId: string;
      payload: Partial<createFormParams>;
    }) => handleUpdateFormService(formId, payload),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_FORMS],
      });

      showSnackbar({
        message: data?.message,
        type: 'success',
      });
    },
  });

  const createFormResponseMutation = useMutation({
    mutationFn: ({
      formId,
      payload,
    }: {
      formId: string;
      payload: Partial<FormResponsePayload>;
    }) => handleSubmitFormResponseService(formId, payload),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_FORMS],
      });
    },
  });

  const shareFormMutation = useMutation({
    mutationFn: ({
      formId,
      payload,
    }: {
      formId: string;
      payload: ShareFormPayload;
    }) => handleShareFormService(formId, payload),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_FORMS],
      });

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_FORM_WITH_ANSWERS],
      });

      showSnackbar({
        message: data?.message,
        type: 'success',
      });
    },
  });

  return {
    changeFormStatusMutation,
    createFormMutation,
    updateFormMutation,
    createFormResponseMutation,
    shareFormMutation,
  };
};
